import { forwardRef } from "react";
import { MapPin, Phone, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GoogleRatingBadge from "@/components/ui/GoogleRatingBadge";
import type { Tables } from "@/integrations/supabase/types";
import truelipsIcon from "@/assets/truelips-icon-new.png";
import { cn } from "@/lib/utils";

type Location = Tables<"locations">;

interface LocationCardProps {
  location: Location;
  onOpenModal?: () => void;
  isHighlighted?: boolean;
}

const LocationCard = forwardRef<HTMLDivElement, LocationCardProps>(
  ({ location, onOpenModal, isHighlighted }, ref) => {
    const googleMapsUrl = location.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${location.name} ${location.address}, ${location.city}, ${location.state}`
    )}`;

    return (
      <div 
        ref={ref}
        className={cn(
          "relative bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-2",
          isHighlighted 
            ? "border-[#ffdb4d] ring-4 ring-[#ffdb4d]/20 scale-[1.02]" 
            : "border-white/20"
        )}
      >

        <div className="flex gap-4">
          {/* Provider Headshot */}
          <div className="flex-shrink-0">
            <img
              src={location.provider_headshot_url || "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop"}
              alt={location.provider_name || location.name}
              className="w-20 h-20 rounded-full object-cover border-2 border-[#ffdb4d]/50"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop";
              }}
            />
          </div>

          {/* Provider Info */}
          <div className="flex-1 min-w-0 pr-16">
            <h3 className="font-bold text-lg text-white leading-tight">{location.name}</h3>
            
            {/* Location */}
            <p className="text-sm text-white/60 mt-2">{location.city}, {location.state}</p>
          </div>
        </div>

        {/* Address & Phone - 3 lines */}
        <div className="mt-4 space-y-1">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 text-sm text-[#ffdb4d] hover:text-[#ffdb4d]/80"
          >
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="flex flex-col">
              <span>{location.address}</span>
              <span>{location.city}, {location.state}</span>
            </div>
          </a>
          {location.phone && (
            <a
              href={`tel:${location.phone.replace(/[^\d]/g, "")}`}
              className="flex items-center gap-2 text-sm text-[#ffdb4d] hover:text-[#ffdb4d]/80 pl-6"
            >
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span>{location.phone}</span>
            </a>
          )}
        </div>

        {/* Google Rating Badge */}
        {location.google_rating && location.google_review_count && (
          <GoogleRatingBadge
            rating={location.google_rating}
            reviewCount={location.google_review_count}
            googleMapsUrl={googleMapsUrl}
            size="sm"
            className="mt-4"
          />
        )}

        {/* Service Badges */}
        <div className="grid grid-cols-2 gap-2 mt-4 sm:flex sm:flex-wrap sm:gap-2">
          {/* TrueWellness - Always show */}
          <Badge variant="outline" className="bg-green-500/20 border-green-400/50 text-green-300 text-xs font-medium px-3 py-1.5 flex items-center justify-center gap-1">
            <Leaf className="h-3 w-3" />
            TrueWellness
          </Badge>
          {location.truetox && (
            <Badge className="bg-[#ffdb4d] text-[#0D1B2A] text-xs font-medium px-3 py-1.5 justify-center">
              TrueTox
            </Badge>
          )}
          {location.truelips && (
            <Badge variant="outline" className="bg-pink-500/20 border-pink-400/50 text-pink-300 text-xs font-medium px-3 py-1.5 flex items-center justify-center gap-1">
              <img src={truelipsIcon} alt="" className="h-3 w-3" />
              TrueLips
            </Badge>
          )}
          {location.trueboost && (
            <Badge variant="outline" className="bg-blue-500/20 border-blue-400/50 text-blue-300 text-xs font-medium px-3 py-1.5 justify-center">
              TrueBoost
            </Badge>
          )}
          {location.truehair && (
            <Badge variant="outline" className="bg-purple-500/20 border-purple-400/50 text-purple-300 text-xs font-medium px-3 py-1.5 justify-center">
              TrueHair
            </Badge>
          )}
        </div>

        {/* CTA Button */}
        <Button
          className="w-full mt-4 bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A] font-semibold"
          asChild
        >
          <Link to={`/locations/${location.slug}`}>
            Meet Your Provider
          </Link>
        </Button>
      </div>
    );
  }
);

LocationCard.displayName = "LocationCard";

export default LocationCard;